import { useNavigate, useParams } from "react-router-dom";
import UtilityPageLayout from "../../../component/layout/UtilityPageLayout";
import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../../stores/store";
import styles from "./CustomStepsPage.module.scss";
import Button from "../../../../../common/components/xepp/button/Button";
import { StepDefinition } from "../../../../../model/workflows/step-definition";
import { SelectItem } from "../../../../../common/components/new/select/Select";
import { removeStep, addStep, updateStep, changeOrderOfSteps } from "../../../../../stores/slices/workflow-slice";
import CreateStepModal from "../modals/create-step-modal/CreateStepModal";
import WorkflowStepCard from "./components/step-card/WorkflowStepCard";
import StepConfigurationPanel from "./components/step-configuration-panel/StepConfigurationPanel";
import { PlusIcon } from "../../../../../assets/icons";
import { SLADurationType } from "../../../../../model/slaDurationType";
import { Department } from "../../../../../model/departments/department";
import { useLazyGetDepartmentsQuery } from "../../../../../service/department-service";
import { v4 as uuidv4 } from "uuid";
import { useEditWorkflowStepsMutation } from "../../../../../service/customersupport-service";
import { hideLoader, showLoader } from "../../../../../stores/slices/loader-slice";
import { ApplicationRoutes } from "../../../../../routes";
import { toast } from "react-toastify";

export const finishAction = new SelectItem(null, "Finish Workflow");

const CustomStepsPage = () => {
	let { workflowId = "" } = useParams();
	const currentState = useSelector((state: RootState) => state.workflow);
	const dispatch = useDispatch();
	const navigator = useNavigate();

	const [selectedStepId, setSelectedStepId] = useState<string | null>(
		currentState.stepDefinitions && currentState.stepDefinitions.length > 0 ? currentState.stepDefinitions[0].id : null
	);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [editingStep, setEditingStep] = useState<StepDefinition | null>(null);

	const [getDepartments, { data: departments, isLoading: isLoadingDepartments, isFetching: isFetchingDepartments }] = useLazyGetDepartmentsQuery();
	const [editWorkflowSteps, { isLoading: isSavingWorkflowSteps }] = useEditWorkflowStepsMutation();

	useEffect(() => {
		getDepartments();
	}, [getDepartments]);

	const selectedStep = currentState.stepDefinitions?.find(step => step.id === selectedStepId);

	const onCancelClick = () => {
		navigator(-1);
	};

	const onCreateStepClick = () => {
		setEditingStep(null);
		setIsCreateModalOpen(true);
	};

	const handleCreateStep = (stepData: { name: string; description: string; departments: Department[] }) => {
		const stepId = uuidv4();
		const newStep: StepDefinition = {
			id: stepId,
			name: stepData.name,
			description: stepData.description,
			stepDefinitionDepartments: stepData.departments.map(dept => ({
				departmentId: dept.id,
				stepDefinitionId: stepId,
				name: dept.name,
			})),
			isDynamic: stepData.departments.length > 1,
			displayOrder: (currentState.stepDefinitions?.length || 0) + 1,
			ableToSeeOtherStepInformation: false,
			ableToSeeCustomerDetails: false,
			assignType: 0,
			additionalServiceRequest: null,
			actions: null,
			createdAt: new Date(),
			utilityId: currentState.utilityId || "",
			isPayRequired: false,
			slaDuration: 0,
			slaDurationType: SLADurationType.Hours,
		};

		dispatch(addStep(newStep));
		setSelectedStepId(newStep.id);
	};

	const handleUpdateStep = (stepId: string, stepData: { name: string; description: string; departments: Department[] }) => {
		const existingStep = currentState.stepDefinitions?.find(s => s.id === stepId);
		if (existingStep) {
			const updatedStep: StepDefinition = {
				...existingStep,
				name: stepData.name,
				description: stepData.description,
				stepDefinitionDepartments: stepData.departments.map(dept => ({
					departmentId: dept.id,
					stepDefinitionId: stepId,
					name: dept.name,
				})),
			};

			dispatch(updateStep(updatedStep));
		}
	};

	const onStepClick = (stepId: string) => {
		setSelectedStepId(stepId);
	};

	const onDeleteStep = (step: StepDefinition) => {
		dispatch(removeStep(step));
		if (selectedStepId === step.id) {
			const remainingSteps = currentState.stepDefinitions?.filter(s => s.id !== step.id) || [];
			setSelectedStepId(remainingSteps.length > 0 ? remainingSteps[0].id : null);
		}
	};

	const onEditStep = (step: StepDefinition) => {
		setEditingStep(step);
		setIsCreateModalOpen(true);
	};

	const dragStep = useRef<{ index: number; stepId: string }>({ index: 0, stepId: "" });
	const draggedOverStep = useRef<{ index: number; stepId: string }>({ index: 0, stepId: "" });
	const isDraggingRef = useRef<boolean>(false);

	const handleStepSort = () => {
		if (!currentState.stepDefinitions || currentState.stepDefinitions.length === 0) return;

		// Get sorted steps to find the actual step IDs
		const sortedSteps = currentState.stepDefinitions.slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

		// Get the step IDs from the sorted array at the drag indices
		const draggedStepId = sortedSteps[dragStep.current.index]?.id;
		const draggedOverStepId = sortedSteps[draggedOverStep.current.index]?.id;

		if (!draggedStepId || !draggedOverStepId) return;

		// Find the original indices in the unsorted array
		const originalSteps = [...currentState.stepDefinitions];
		const draggedIndex = originalSteps.findIndex(s => s.id === draggedStepId);
		const draggedOverIndex = originalSteps.findIndex(s => s.id === draggedOverStepId);

		if (draggedIndex === -1 || draggedOverIndex === -1) return;

		var newSteps = [...originalSteps];
		const draggedItem = newSteps[draggedIndex];
		// remove the dragged item
		newSteps.splice(draggedIndex, 1);
		// insert it at the new position
		newSteps.splice(draggedOverIndex, 0, draggedItem);

		// Update displayOrder for all steps
		newSteps = newSteps.map((x, i) => {
			return { ...x, displayOrder: i + 1 };
		});

		dispatch(changeOrderOfSteps({ steps: newSteps }));

		// Prevent click event from firing after drag
		setTimeout(() => {
			isDraggingRef.current = false;
		}, 100);
	};

	const handleSaveWorkflow = async () => {
		const sortedSteps = [...(currentState.stepDefinitions ?? [])].sort(
			(a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
		);

		for (const step of sortedSteps) {
			if (!step.actions || step.actions.length === 0) {
				toast.error(`Step "${step.name}" must have at least one action defined.`);
				return;
			}
			if (!step.slaDuration || step.slaDuration === 0) {
				toast.error(`Step "${step.name}" must have an SLA duration set.`);
				return;
			}
		}

		if (sortedSteps[0]?.stepDefinitionDepartments?.length > 1) {
			toast.error("The first step can only have one department assigned.");
			return;
		}

		dispatch(showLoader());
		try {

			const stepDefinitionsWithNextStep = sortedSteps.map((step, index) => {
				const nextStep = sortedSteps[index + 1] ?? null;
				return {
					...step,
					actions: step.actions?.map(action => ({
						...action,
						nextStepDefinitionId: nextStep?.id ?? null,
					})) ?? null,
				};
			});

			await editWorkflowSteps({
				workflowId: workflowId,
				stepDefinitions: stepDefinitionsWithNextStep,
			}).unwrap();
			navigator(`../${ApplicationRoutes.WORKFLOWS_MANAGMENT}/`);
			toast.success("Workflow saved successfully.");
		} catch (error: any) {
			if (error?.status === 400 && error?.data?.errors) {
				const errorMessages: string[] = [];

				Object.keys(error.data.errors).forEach(field => {
					const fieldErrors = error.data.errors[field];
					if (Array.isArray(fieldErrors)) {
						errorMessages.push(...fieldErrors);
					}
				});

				errorMessages.forEach(message => {
					toast.error(message);
				});
			} else {
				toast.error(error?.data?.title || "Failed to save workflow. Please try again.");
			}
		} finally {
			dispatch(hideLoader());
		}
	};

	// Get available steps for dropdown (excluding current step)
	const getAvailableSteps = (): SelectItem[] => {
		var availableSteps = currentState.stepDefinitions?.filter(step => step.id !== selectedStepId).map(step => new SelectItem(step.id, step.name)) || [];
		availableSteps.push(finishAction);
		return availableSteps;
	};

	return (
		<UtilityPageLayout title="">
			<div className={styles.pageContainer}>
				{/* Left Sidebar - Workflow Steps List */}
				<div className={styles.stepsContainer}>
					{/* Workflow Info Section */}
					<div className={styles.workflowInfo}>
						<div className={styles.workflowName}>{currentState.name}</div>
						<div className={styles.workflowDescription}>{currentState.description}</div>
						{currentState.serviceRequest && (
							<div className={styles.serviceRequestInfo}>
								<span className={styles.label}>Service Request:</span>
								<span className={styles.value}>{currentState.serviceRequest.name}</span>
							</div>
						)}
					</div>
					<div className={styles.stepsHeader}>
						<div className={styles.stepsTitle}>Workflow Steps</div>
						<Button
							text="Add New Step"
							variant="secondary"
							leftIcon={<PlusIcon color="white" fill="white" />}
							onClick={onCreateStepClick}
							className={styles.addStepButton}
						/>
					</div>
					<div className={styles.stepsList}>
						{currentState.stepDefinitions
							?.slice()
							.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
							.map((step, index) => (
								<div
									key={step.id}
									draggable
									onDragStart={() => {
										dragStep.current = { index: index, stepId: step.id };
										isDraggingRef.current = true;
									}}
									onDragEnter={() => {
										draggedOverStep.current = { index: index, stepId: step.id };
									}}
									onDragEnd={() => {
										handleStepSort();
									}}
									onDragOver={e => {
										e.preventDefault();
									}}
								>
									<WorkflowStepCard
										step={step}
										index={index}
										isActive={selectedStepId === step.id}
										departments={departments}
										onClick={() => {
											if (!isDraggingRef.current) {
												onStepClick(step.id);
											}
										}}
										onEdit={e => {
											e.stopPropagation();
											onEditStep(step);
										}}
										onDelete={e => {
											e.stopPropagation();
											onDeleteStep(step);
										}}
										onReorder={e => {
											e.stopPropagation();
										}}
									/>
								</div>
							))}
					</div>
					<div className={styles.stepsFooter}>
						<Button text="Cancel Setup" onClick={onCancelClick} variant="outline" />
						<Button text="Save Workflow" onClick={handleSaveWorkflow} variant="secondary" />
					</div>
				</div>

				{/* Right Panel - Step Configuration */}
				<StepConfigurationPanel step={selectedStep || null} availableSteps={getAvailableSteps()} onCancel={onCancelClick} onCreateStep={onCreateStepClick} />
			</div>

			{/* Create/Edit Step Modal */}
			<CreateStepModal
				isOpen={isCreateModalOpen}
				onClose={() => {
					setIsCreateModalOpen(false);
					setEditingStep(null);
				}}
				onCreate={handleCreateStep}
				onUpdate={handleUpdateStep}
				editingStep={editingStep}
				availableDepartments={departments}
				isFirstStep={(() => {
					if (!editingStep) return !currentState.stepDefinitions || currentState.stepDefinitions.length === 0;
					const sorted = [...(currentState.stepDefinitions || [])].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
					return sorted[0]?.id === editingStep.id;
				})()}
			/>
		</UtilityPageLayout>
	);
};

export default CustomStepsPage;
